import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Alert, 
  Spinner,
  Badge,
  Button,
  Modal
} from 'react-bootstrap';
import axios from 'axios';

const Webhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPayloadModal, setShowPayloadModal] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState(null);

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/webhooks');
      setWebhooks(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching webhooks:', error);
      setError('Failed to fetch webhooks: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'success': 'success',
      'error': 'danger',
      'pending': 'warning',
      'processing': 'info'
    };
    return statusMap[status] || 'secondary';
  };

  const truncatePayload = (payload, maxLength = 100) => {
    if (!payload) return 'N/A';
    if (typeof payload === 'string') {
      return payload.length > maxLength ? payload.substring(0, maxLength) + '...' : payload;
    }
    const payloadStr = JSON.stringify(payload);
    return payloadStr.length > maxLength ? payloadStr.substring(0, maxLength) + '...' : payloadStr;
  };

  const handlePayloadClick = (payload) => {
    setSelectedPayload(payload);
    setShowPayloadModal(true);
  };

  const formatJsonPayload = (payload) => {
    if (!payload) return 'No payload data';
    
    try {
      if (typeof payload === 'string') {
        return JSON.stringify(JSON.parse(payload), null, 2);
      }
      return JSON.stringify(payload, null, 2);
    } catch (error) {
      return payload.toString();
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" variant="warning" />
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Webhook Logs</h2>
            <Button 
              variant="outline-primary" 
              onClick={fetchWebhooks}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
            </Button>
          </div>

          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Card>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Payload</th>
                    <th>Processed At</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {webhooks.map((webhook) => (
                    <tr key={webhook.id}>
                      <td>
                        <code>{webhook.id}</code>
                      </td>
                      <td>
                        <Badge bg="info">{webhook.webhook_type || 'N/A'}</Badge>
                      </td>
                      <td>
                        <Badge bg={getStatusBadge(webhook.status)}>
                          {webhook.status || 'N/A'}
                        </Badge>
                      </td>
                      <td>
                        <div 
                          style={{ 
                            maxWidth: '300px', 
                            wordBreak: 'break-word',
                            cursor: 'pointer',
                            color: '#007bff',
                            textDecoration: 'underline'
                          }}
                          onClick={() => handlePayloadClick(webhook.payload)}
                          title="Click to view full payload"
                        >
                          {truncatePayload(webhook.payload)}
                        </div>
                      </td>
                      <td>
                        {formatDate(webhook.processed_at)}
                      </td>
                      <td>
                        {formatDate(webhook.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {webhooks.length === 0 && (
                <div className="text-center text-muted py-4">
                  No webhook logs found. Webhook activity will appear here.
                </div>
              )}
            </Card.Body>
          </Card>

          <div className="mt-3">
            <small className="text-muted">
              Showing {webhooks.length} webhook logs (ordered by most recent first)
            </small>
          </div>
        </Col>
      </Row>

      {/* Payload View Modal */}
      <Modal 
        show={showPayloadModal} 
        onHide={() => setShowPayloadModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Webhook Payload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '15px', 
            borderRadius: '5px',
            fontFamily: 'monospace',
            fontSize: '12px',
            maxHeight: '500px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap'
          }}>
            {formatJsonPayload(selectedPayload)}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-primary" 
            onClick={() => {
              navigator.clipboard.writeText(formatJsonPayload(selectedPayload));
            }}
          >
            📋 Copy JSON
          </Button>
          <Button variant="secondary" onClick={() => setShowPayloadModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Webhooks; 